import React, { useState } from 'react';

import { useIsMutating, useQuery } from '@tanstack/react-query';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';

import useAttachmentsUploader from 'components/hooks/useAttachmentsUploader';
import usePdfMutations from 'components/hooks/usePdfMutations';

import OutlinedButton from 'components/application/buttons/OutlinedButton';

import { useBreadBoard } from 'components/contexts/Toaster';
import AdditionalProjectDocumentsRow from 'components/projectDocuments/AdditionalProjectDocumentsRow';
import DefaultProjectDocumentsRow from 'components/projectDocuments/DefaultProjectDocumentsRow';
import DestroyProjectDocumentModal from 'components/projectDocuments/DestroyProjectDocumentModal';
import ReorderDocumentsModal from 'components/projectDocuments/ReorderDocumentsModal';

import AddIcon from '-!svg-react-loader?name=AddIcon!icons/add.svg';
import ProcoreLogo from '-!svg-react-loader?name=ProcoreLogo!icons/procore_logo.svg';
import ReorderIcon from '-!svg-react-loader?name=ReorderIcon!icons/low_priority.svg';

import { resourceShape } from 'components/helpers/serialisableResources';

export default function ProjectDocumentsTab({
  procoreProjectIntegration,
  project,
  riskAssessmentOnlyAccess,
}) {
  const breadBoard = useBreadBoard();
  const isMutating = useIsMutating();
  const projectId = project?.data?.id;

  const {
    createPdfMutation,
    deletePdfMutation,
    updateCoverSheetSelectionMutation,
    updatePdfPositionMutation,
  } = usePdfMutations({ projectId });

  const [isReorderDocumentsModalOpen, setIsReorderDocumentsModalOpen] =
    useState(false);

  const [
    isDestroyProjectDocumentModalOpen,
    setIsDestroyProjectDocumentModalOpen,
  ] = useState(false);

  const [selectedPdfId, setSelectedPdfId] = useState(null);

  const { data: projectPdfs, isSuccess: isProjectPdfsFetchSuccess } = useQuery({
    queryKey: ['projectPdfs', project.data.id],
    queryFn: async () => {
      const response = await axios.get(`/projects/${project.data.id}/pdfs`);
      return response.data;
    },
    onError: breadBoard.addInedibleToast,
    refetchInterval: (data) => {
      if (data?.meta?.defaultPdfsPending) return 1000;
      return false;
    },
  });

  const handleUpdateCoverSheetSelection = ({ pdfId, selected }) =>
    updateCoverSheetSelectionMutation.mutate(
      { pdfId, selected },
      {
        onSuccess: () =>
          window.location.replace(
            `/projects/${projectId}?switch_tab=documents-tab`,
          ),
        onError: breadBoard.addInedibleToast,
      },
    );

  const handlePdfpositionOrderSubmit = (reorderedPdfIds) =>
    updatePdfPositionMutation.mutate(
      {
        pdf_position_configuration: {
          pdf_attributes: Object.fromEntries(
            reorderedPdfIds.map((pdfId, index) => [
              pdfId,
              { position: index + 1 },
            ]),
          ),
        },
      },
      {
        onSuccess: () =>
          window.location.replace(
            `/projects/${projectId}?switch_tab=documents-tab`,
          ),
        onError: breadBoard.addInedibleToast,
        onSettled: () => setIsReorderDocumentsModalOpen(false),
      },
    );

  const { handleFilesSelect } = useAttachmentsUploader({
    type: 'pdf',
    onUploaderMutationSuccess: (uploadKey) =>
      createPdfMutation.mutateAsync(uploadKey, {
        onSuccess: () =>
          window.location.replace(
            `/projects/${projectId}?switch_tab=documents-tab`,
          ),
      }),
    onUploaderMutationError: breadBoard.addInedibleToast,
    onUploaderQueryError: breadBoard.addInedibleToast,
  });

  const handleDeletePdfSubmit = () =>
    deletePdfMutation.mutate(selectedPdfId, {
      onSuccess: () =>
        window.location.replace(
          `/projects/${projectId}?switch_tab=documents-tab`,
        ),
      onError: breadBoard.addInedibleToast,
      onSettled: () => {
        setSelectedPdfId(null);
        setIsDestroyProjectDocumentModalOpen(false);
      },
    });

  const handleReorderDocumentsModalOpen = () =>
    setIsReorderDocumentsModalOpen(true);

  const handleReorderDocumentsModalClose = () =>
    setIsReorderDocumentsModalOpen(false);

  const handleDeleteIconClick = (pdfId) => {
    setSelectedPdfId(pdfId);
    setIsDestroyProjectDocumentModalOpen(true);
  };

  const handleDeletePdfModalClose = () =>
    setIsDestroyProjectDocumentModalOpen(false);

  const projectPdfsLength = projectPdfs?.data?.length;
  const shouldShowCoversheet = projectPdfsLength > 0;

  const shouldBlockMutations =
    isMutating > 0 ||
    !isProjectPdfsFetchSuccess ||
    projectPdfs?.meta?.defaultPdfsPending;

  return (
    <>
      {isReorderDocumentsModalOpen && (
        <ReorderDocumentsModal
          isUpdatePdfPositionMutationLoading={
            updatePdfPositionMutation.isLoading
          }
          onClose={handleReorderDocumentsModalClose}
          onSave={handlePdfpositionOrderSubmit}
          projectPdfs={projectPdfs?.data}
        />
      )}
      {isDestroyProjectDocumentModalOpen && (
        <DestroyProjectDocumentModal
          isDeletePdfMutationLoading={deletePdfMutation.isLoading}
          onClose={handleDeletePdfModalClose}
          onDestroySubmit={handleDeletePdfSubmit}
        />
      )}
      <div className='tw-border-l tw-border-r tw-flex tw-h-14 tw-min-h-14 tw-w-full tw-min-w-full tw-items-center tw-justify-end tw-border-0 tw-border-l-1 tw-border-r-1 tw-border-t-1 tw-border-solid tw-border-grey-100 tw-bg-white tw-px-5'>
        {procoreProjectIntegration?.data?.attributes.publishedAt &&
          procoreProjectIntegration?.data?.attributes.version && (
            <div className='tw-mr-auto tw-flex tw-h-8 tw-items-center'>
              <ProcoreLogo className='tw-h-4 tw-w-4' />
              <span className='tw-ml-2.5'>
                Version
                <span>
                  {` ${procoreProjectIntegration.data.attributes.version} published to Procore on ${moment(procoreProjectIntegration.data.attributes.publishedAt).format('DD MMM YYYY') || ''}`}
                </span>
              </span>
            </div>
          )}
        <div className='tw-mr-4'>
          <OutlinedButton
            color='grey'
            disabled={shouldBlockMutations || projectPdfsLength < 2}
            onClick={handleReorderDocumentsModalOpen}
            size='sm'
          >
            <ReorderIcon className='tw--ml-2' height={24} width={24} />
            <span>Reorder documents</span>
          </OutlinedButton>
        </div>
        <PdfUploadButton
          isDisabled={shouldBlockMutations}
          onFileSelect={(acceptedFiles) => handleFilesSelect(acceptedFiles)}
        />
      </div>
      <table className='table handshq__table tw-border-grey-100 tw-bg-white'>
        <thead className='handshq__thead'>
          <tr className='tw-bg-grey-050'>
            <th className='tw-pl-5 tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900'>
              Document
            </th>
            <th className='tw-w-36 tw-text-center tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900'>
              Actions
            </th>
            {shouldShowCoversheet && (
              <th className='tw-w-30 tw-text-center tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900'>
                Cover sheet
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          <DefaultProjectDocumentsRow
            documentName='Risk assessment'
            href={`/projects/${project.data.id}/risk_assessment`}
            shouldShowCoversheet={shouldShowCoversheet}
          />
          {riskAssessmentOnlyAccess && (
            <>
              <DefaultProjectDocumentsRow
                documentName='Sequence of operations'
                href={`/projects/${project.data.id}/sequence_of_operations`}
                shouldShowCoversheet={shouldShowCoversheet}
              />
              <DefaultProjectDocumentsRow
                documentName='Method statement'
                href={`/projects/${project.data.id}/method`}
                shouldShowCoversheet={shouldShowCoversheet}
              />
            </>
          )}
          {projectPdfs?.meta?.defaultPdfsPending && projectPdfsLength === 0 ?
            <tr className='hover:tw-bg-grey-025'>
              <td
                className='tw-border-grey-100 tw-text-center tw-align-middle tw-font-medium tw-text-grey-900'
                colSpan={projectPdfsLength > 0 ? 3 : 2}
              >
                Default PDFs are currently being added to your project
              </td>
            </tr>
          : projectPdfs?.data?.map((pdf) => (
              <AdditionalProjectDocumentsRow
                key={pdf.id}
                onDeleteIconClick={handleDeleteIconClick}
                onUpdateCoverSheetSelection={handleUpdateCoverSheetSelection}
                pdf={pdf}
                shouldBlockMutations={shouldBlockMutations}
              />
            ))
          }
        </tbody>
      </table>
    </>
  );
}

function PdfUploadButton({ isDisabled, onFileSelect }) {
  const { getInputProps, getRootProps, open } = useDropzone({
    accept: ['application/pdf'],
    multiple: false,
    maxSize: 10000000,
    maxUploads: 1,
    onDrop: onFileSelect,
    noClick: true,
    noKeyboard: true,
  });

  return (
    <div {...getRootProps()}>
      <input {...getInputProps()} />
      <OutlinedButton
        className='tw-apply-outlined-button tw-apply-outlined-button--on-light tw-apply-outlined-button--sm tw-apply-outlined-button--grey'
        disabled={isDisabled}
        onClick={open}
        type='button'
      >
        <AddIcon className='tw--ml-2' height={24} width={24} />
        Upload PDF
      </OutlinedButton>
    </div>
  );
}

ProjectDocumentsTab.propTypes = {
  procoreProjectIntegration: PropTypes.shape({
    data: resourceShape('procoreProjectIntegration'),
  }),
  project: PropTypes.shape({
    data: resourceShape('simpleProject'),
  }).isRequired,
  riskAssessmentOnlyAccess: PropTypes.bool.isRequired,
};

PdfUploadButton.propTypes = {
  isDisabled: PropTypes.bool,
  onFileSelect: PropTypes.func.isRequired,
};
